import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Card, CardBody, CardHeader } from '../../components/ui/Card';
import { useCurrentOrg } from '../orgs/useCurrentOrg';
import { listReviews, type PausedRun } from './api';

export function ReviewsPage() {
  const { org } = useCurrentOrg();
  const orgId = org?.id ?? '';
  const reviews = useQuery({
    queryKey: ['reviews', orgId],
    queryFn: () => listReviews(orgId),
    enabled: !!orgId,
    refetchInterval: 15_000,
  });

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader
          title="Reviews queue"
          description="Runs paused for human review. Each entry was suspended by an agent (typically Compliance) and needs your verdict before it can continue."
        />
        <CardBody>
          {reviews.isLoading && <p className="text-sm text-ink-tertiary">Loading…</p>}
          {reviews.data && reviews.data.length === 0 && (
            <p className="text-sm text-ink-tertiary">
              Nothing waiting. When an agent calls <code>request_human_review</code>, its run shows up here.
            </p>
          )}
          {reviews.data && reviews.data.length > 0 && (
            <ul className="divide-y divide-line-soft">
              {reviews.data.map((r) => (
                <ReviewRow key={r.id} review={r} />
              ))}
            </ul>
          )}
        </CardBody>
      </Card>
    </div>
  );
}

function ReviewRow({ review }: { review: PausedRun }) {
  const title = review.pause_title || (review.pause_payload?.title as string) || review.objective;
  return (
    <li className="py-3">
      <Link
        to={`/reviews/${review.id}`}
        className="flex items-start justify-between gap-3 rounded-lg px-2 -mx-2 hover:bg-surface-soft/40"
      >
        <div className="min-w-0">
          <p className="truncate text-sm font-semibold text-ink-primary">{title}</p>
          <p className="mt-0.5 text-xs text-ink-secondary">
            <span className="font-mono uppercase text-ink-tertiary">{review.agent_template}</span>
            {' · '}
            {review.agent_name}
          </p>
          {review.objective && (
            <p className="mt-1 line-clamp-2 text-xs text-ink-tertiary">{review.objective}</p>
          )}
        </div>
        <span className="shrink-0 font-mono text-[10px] text-ink-tertiary">
          {timeAgo(review.paused_at)}
        </span>
      </Link>
    </li>
  );
}

function timeAgo(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const s = Math.round(diff / 1000);
  if (s < 60) return `${s}s ago`;
  const m = Math.round(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.round(h / 24);
  return `${d}d ago`;
}
